import { notEmpty } from "@product/scmp-sdk";
import { useAsync, useMountEffect, useSyncedRef } from "@react-hookz/web";
import YouTube from "@vip30/react-youtube";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import type { YouTubePlayer, Options as YoutubePlayerOptions } from "youtube-player/dist/types";

import type { VideoPlayMode } from "~/components/scmp-youtube-video/enums";
import { data as appData, config } from "~/data";
import { useIntersection } from "~/lib/hooks";
import type { scmpYoutubeVideoPlayerQuery } from "~/queries/__generated__/scmpYoutubeVideoPlayerQuery.graphql";

import { AutoplayMode } from "./enums";
import {
  useAdManagerControl,
  useAdManualPauseEffect,
  useImaIntegration,
  useIntersectionHandler,
  useManualPlayEffect,
  useOnlyOneYoutubePlayerEffect,
  useYouTubeControl,
} from "./hooks";
import {
  AdContainer,
  AdControlButtonContainer,
  Container,
  StyledMuteIcon,
  StyledUnmuteIcon,
  StyledYouTube,
  VideoContainer,
} from "./styles";

export type Props = {
  adInfo?: ADInfo;
  allow?: string;
  allowfullscreen?: string;
  className?: string;
  enableAutoPlay: boolean;
  entityId?: string;
  frameborder?: string;
  gaActionPrefix?:
    | `Youtube ${"article body" | "article header" | "article footer" | "footer widget"}/`
    | "";
  height?: string;
  index: number;
  isEnabledImaManager?: boolean;
  isMuteVideo?: boolean;
  relatedChannels?: string[];
  title?: string;
  videoId: string;
  videoPlayMode: VideoPlayMode;
  width?: string;
};

export const SCMPYouTubeVideoPlayer: FunctionComponent<Props> = ({
  adInfo,
  className,
  enableAutoPlay,
  entityId,
  gaActionPrefix = "Youtube article body/",
  height,
  index,
  isEnabledImaManager = true,
  isMuteVideo = true,
  relatedChannels = [appData.youtube.scmpChannelId],
  title,
  videoId,
  videoPlayMode,
  width,
}) => {
  const environment = useRelayEnvironment();
  const [{ result: data }, { execute }] = useAsync(() =>
    fetchQuery<scmpYoutubeVideoPlayerQuery>(
      environment,
      graphql`
        query scmpYoutubeVideoPlayerQuery($applicationId: String!, $entityId: String!) {
          content(
            contentType: VIDEO
            filter: { entityId: $entityId, applicationId: $applicationId }
          ) {
            headline
            sections {
              value {
                name
              }
            }
          }
        }
      `,
      {
        applicationId: appData.application.scmp.entityUuid,
        entityId: entityId ?? "",
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise(),
  );

  useMountEffect(() => {
    if (!notEmpty(entityId)) return;
    void execute();
  });

  // Can play same video in different places
  const uniqueVideoId = ["yt", videoId, entityId, index].filter(notEmpty).join("-");
  const adElementId = `ad-${uniqueVideoId}`;
  const { captureIntersectionElement, intersection } = useIntersection({
    threshold: 0.45,
  });

  const trackingData = useMemo(
    () => ({
      gaActionPrefix,
      sectionString:
        first(data?.content?.sections)
          ?.value?.map(section => section?.name)
          .join("|") ?? "",
      youTubeVideoId: videoId,
      youTubeVideoTitle: title ?? "",
    }),
    [data?.content?.sections, gaActionPrefix, title, videoId],
  );

  const {
    handleOnEnd,
    handleOnPause,
    handleOnReady,
    handleOnStateChange,
    manualPaused,
    player,
    setAutoplayMode,
    setManualPaused,
  } = useYouTubeControl(uniqueVideoId, trackingData);
  useManualPlayEffect(videoPlayMode, player);

  const zone = adInfo?.zone ?? `video/article/instream1`;
  const adIu = `${config.advertisement.googlePublisherTag.networkId}/${config.advertisement.googlePublisherTag.adUnitPrefix}/${zone}`;

  const adData = useMemo(
    () => ({
      adInfo,
      iu: adIu,
    }),
    [adInfo, adIu],
  );
  const { adsManager, initImaManager, isAdPlaying, isAdStarted, isImaInitialized } =
    useImaIntegration({
      adData,
      isIntersecting: intersection?.isIntersecting,
      player,
      trackingData,
    });

  useAdManualPauseEffect({
    setManualPaused,
  });

  const playerOptions: YoutubePlayerOptions = {
    embedConfig: {
      adsConfig: {
        adTagParameters: {
          iu: adIu,
        },
        disableAds: true,
        nonPersonalizedAd: true,
      },
      disabledRelatedVideos: true,
      enableIma: true,
      relatedChannels,
    },
    height,
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: enableAutoPlay ? 1 : 0,
      origin: window.location.origin,
      playsinline: 1,
      rel: 0,
    },
    width,
  };

  const latestIsAdStartedReference = useSyncedRef(isAdStarted);
  const latestIsAdPlayingReference = useSyncedRef(isAdPlaying);

  const { handleIsIntersecting, handleIsNotIntersecting } = useIntersectionHandler({
    adsManager,
    latestIsAdPlayingReference,
    latestIsAdStartedReference,
    player,
    setAutoplayMode,
  });

  const isIntersectionAndAdAndPlayerReady = intersection && isImaInitialized && player;

  const latestManualPausedReference = useSyncedRef(manualPaused);
  const shouldTriggerIntersectionHandler = enableAutoPlay && !latestManualPausedReference.current;

  useEffect(() => {
    if (!isIntersectionAndAdAndPlayerReady) {
      return;
    }

    const run = async () => {
      const iframe = await player?.getIframe();
      if (!iframe || !shouldTriggerIntersectionHandler) {
        setAutoplayMode(AutoplayMode.Unset);
        const playerState: number = await player?.getPlayerState();
        if (playerState === YouTube.PlayerState.PLAYING || latestIsAdPlayingReference.current) {
          setManualPaused(false);
        }
        return;
      }

      if (intersection.isIntersecting) {
        handleIsIntersecting();
      } else {
        handleIsNotIntersecting();
      }
    };

    void run();
  }, [
    handleIsIntersecting,
    handleIsNotIntersecting,
    intersection,
    isIntersectionAndAdAndPlayerReady,
    latestIsAdPlayingReference,
    player,
    setAutoplayMode,
    setManualPaused,
    shouldTriggerIntersectionHandler,
  ]);

  useOnlyOneYoutubePlayerEffect({ player, setAutoplayMode, uniqueVideoId });

  const { handleChangeVolume, isMute } = useAdManagerControl(adsManager, player);

  return (
    <Container className={className}>
      <VideoContainer ref={captureIntersectionElement}>
        <StyledYouTube
          id={uniqueVideoId}
          opts={playerOptions}
          title={data?.content?.headline ?? ""}
          videoId={videoId}
          onEnd={handleOnEnd}
          onPause={() => {
            // Prevent when ad started trigger the pause event
            !isAdStarted && handleOnPause();
          }}
          onReady={(event: { target: YouTubePlayer }) => {
            isMuteVideo && void event.target.mute();
            isEnabledImaManager && initImaManager(event.target, uniqueVideoId, adElementId);
            handleOnReady(event);
          }}
          onStateChange={handleOnStateChange}
        />
        <AdContainer id={adElementId} />
        {isAdStarted && (
          <AdControlButtonContainer>
            {isMute ? (
              <StyledUnmuteIcon onClick={() => handleChangeVolume(false)} />
            ) : (
              <StyledMuteIcon onClick={() => handleChangeVolume(true)} />
            )}
          </AdControlButtonContainer>
        )}
      </VideoContainer>
    </Container>
  );
};

SCMPYouTubeVideoPlayer.displayName = "SCMPYouTubeVideoPlayer";
